import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { AUTH } from "@/constants/testIds";
import { ArrowRight, AlertCircle, ShieldCheck, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  useSEO({
    title: "Sign in — ErrandDay",
    description: "Sign in to ErrandDay to manage your verified errands, track agents, and approve photo-proof completions.",
    noindex: true,
    canonicalPath: "/login",
  });
  const { refreshMe } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [twofa, setTwofa] = useState(null);   // { challenge_id }
  const [code, setCode] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError] = useState(
    params.get("google") === "failed" ? "Google sign-in failed. Please try again." :
    params.get("reset") === "ok" ? "Password updated — sign in with your new password." : ""
  );
  const [busy, setBusy] = useState(false);

  const finalizeLogin = async (user) => {
    await refreshMe();
    if (user.role === "agent") nav("/agent");
    else if (user.role === "admin") nav("/admin");
    else nav("/dashboard");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: remember });
      if (data.requires_2fa) {
        setTwofa({ challenge_id: data.challenge_id });
        toast.success("Verification code emailed");
      } else {
        await finalizeLogin(data.user);
      }
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id: twofa.challenge_id, code });
      await finalizeLogin(data.user);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const requestMagic = async () => {
    if (!email) { setError("Enter your email above first"); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/magic-link", { email });
      setMagicSent(true);
      toast.success("Magic link sent");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onGoogle = () => {
    sessionStorage.setItem("eb_google_role", "client");
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex md:w-5/12 relative overflow-hidden border-r border-[var(--eb-border)]">
        <img
          src="https://images.unsplash.com/photo-1559833064-6f4573ec1ac9?w=1200&q=80&auto=format&fit=crop"
          alt="Lagos cityscape"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--eb-bg)] via-[var(--eb-bg)]/80 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-[var(--eb-primary)] flex items-center justify-center text-white font-black">ED</div>
            <span className="font-[800] text-lg tracking-tight">ErrandDay</span>
          </Link>
          <h1 className="text-4xl md:text-5xl leading-tight">Welcome back to <span className="text-[var(--eb-primary)]">family</span>.</h1>
          <p className="mt-4 text-[var(--eb-text-dim)] max-w-md">Pick up where you left off — your tasks, your agents, your peace of mind.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md eb-fadein">
          <Link to="/" className="md:hidden inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-md bg-[var(--eb-primary)] flex items-center justify-center text-white font-black">ED</div>
            <span className="font-[800] text-lg">ErrandDay</span>
          </Link>
          <span className="eb-chip">{twofa ? "Two-factor" : "Sign in"}</span>
          <h2 className="mt-4 text-3xl">{twofa ? "Enter the code we emailed you" : "Sign in to your account"}</h2>
          {!twofa && (
            <p className="mt-2 text-[var(--eb-text-dim)] text-sm">
              Or <Link to="/register" data-testid={AUTH.toggleLink} className="text-[var(--eb-primary)] hover:underline">create a new one</Link>.
            </p>
          )}

          {!twofa ? (
            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div>
                <label className="eb-label">Email</label>
                <input
                  data-testid={AUTH.emailInput}
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@diaspora.com" className="eb-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="eb-label !mb-0">Password</label>
                  <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-[var(--eb-primary)] hover:underline">Forgot?</Link>
                </div>
                <input
                  data-testid={AUTH.passwordInput}
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="eb-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--eb-text-dim)] cursor-pointer">
                <input data-testid="login-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[var(--eb-primary)]" />
                Remember me for 30 days
              </label>
              {error && (
                <div data-testid={AUTH.errorAlert} className="flex items-start gap-2 text-sm text-[var(--eb-secondary)] bg-[var(--eb-secondary)]/10 border border-[var(--eb-secondary)]/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}
              <button data-testid={AUTH.submitBtn} disabled={busy} type="submit" className="eb-btn eb-btn-primary w-full !py-3 mt-2">
                {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          ) : (
            <form onSubmit={submit2FA} className="mt-7 space-y-4">
              <p className="text-sm text-[var(--eb-text-dim)]">We sent a 6-digit code to your email. It expires in 5 minutes.</p>
              <div>
                <label className="eb-label">6-digit code</label>
                <input
                  data-testid="login-2fa-code"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric" autoFocus placeholder="000000"
                  className="eb-input text-center text-2xl tracking-[1rem] font-bold"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-[var(--eb-secondary)] bg-[var(--eb-secondary)]/10 border border-[var(--eb-secondary)]/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}
              <button data-testid="login-2fa-submit" disabled={busy || code.length !== 6} type="submit" className="eb-btn eb-btn-primary w-full !py-3">
                {busy ? "Verifying…" : (<><ShieldCheck className="w-4 h-4" /> Verify and sign in</>)}
              </button>
              <button type="button" onClick={() => { setTwofa(null); setCode(""); }} className="eb-btn eb-btn-ghost w-full">
                Use a different account
              </button>
            </form>
          )}

          {!twofa && (
            <>
              <div className="my-6 flex items-center gap-3 text-xs text-[var(--eb-text-dim)]">
                <div className="eb-divider flex-1" /> OR <div className="eb-divider flex-1" />
              </div>
              <div className="space-y-2">
                <button data-testid={AUTH.googleBtn} onClick={onGoogle} className="eb-btn eb-btn-ghost w-full !py-3">
                  <GoogleGlyph /> Continue with Google
                </button>
                <button
                  data-testid="login-magic-btn"
                  onClick={requestMagic}
                  disabled={busy || magicSent}
                  className="eb-btn eb-btn-ghost w-full !py-3"
                >
                  {magicSent
                    ? <><Mail className="w-4 h-4 text-[var(--eb-primary)]" /> Magic link sent — check your inbox</>
                    : busy
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Mail className="w-4 h-4" /> Email me a magic link instead</>}
                </button>
              </div>
            </>
          )}

          <p className="mt-8 text-xs text-[var(--eb-text-faint)] text-center">
            By signing in you agree to our trust & safety code. Your funds remain in escrow until you approve.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4">
      <path fill="#0E7A3D" d="M21.35 11.1H12v3.2h5.35c-.23 1.5-1.6 4.4-5.35 4.4a6 6 0 1 1 0-12 5.4 5.4 0 0 1 3.8 1.45l2.6-2.5A9.7 9.7 0 0 0 12 2.5a9.5 9.5 0 1 0 0 19c5.48 0 9.1-3.84 9.1-9.27 0-.65-.07-1.13-.17-1.63z" />
    </svg>
  );
}
